import React, { useEffect, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import api, { fileUrl } from "@/lib/api";
import { motion } from "framer-motion";
import { toast } from "sonner";
import {
  Camera,
  ArrowRight,
  Sparkles,
  Users,
  Calendar,
  Grid3x3,
} from "lucide-react";

/**
 * Guest landing: enter name, then go to capture screen.
 */
export default function GuestLandingPage() {
  const { slug } = useParams();
  const [event, setEvent] = useState(null);
  const [error, setError] = useState(null);
  const [name, setName] = useState(
    localStorage.getItem(`guest_name_${slug}`) || ""
  );
  const nav = useNavigate();

  useEffect(() => {
    api
      .get(`/public/events/${slug}`)
      .then((r) => setEvent(r.data))
      .catch((e) =>
        setError(e.response?.data?.detail || "Evento não encontrado")
      );
  }, [slug]);

  const enter = (e) => {
    e.preventDefault();
    const clean = name.trim();
    if (!clean) {
      toast.error("Digite seu nome");
      return;
    }
    localStorage.setItem(`guest_name_${slug}`, clean);
    nav(`/e/${slug}/capture`);
  };

  if (error) {
    return (
      <div className="min-h-[100dvh] text-white flex items-center justify-center px-6">
        <div className="glass rounded-2xl p-8 max-w-md text-center">
          <h1 className="font-display font-bold text-xl tracking-tight">
            Ops!
          </h1>
          <p className="text-white/60 mt-2">{error}</p>
        </div>
      </div>
    );
  }
  if (!event)
    return (
      <div className="min-h-[100dvh] text-white flex items-center justify-center">
        <div className="animate-pulse text-white/50">Carregando evento...</div>
      </div>
    );

  return (
    <div className="min-h-[100dvh] text-white grain flex flex-col">
      <div className="flex-1 flex items-center justify-center px-6 py-10">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="max-w-md w-full"
        >
          {event.is_vip && (
            <div className="text-center mb-4">
              <span className="badge-vip">
                <Sparkles className="w-3 h-3" /> Evento VIP
              </span>
            </div>
          )}

          <div className="text-center mb-8">
            <span className="text-xs uppercase tracking-[0.2em] text-[#00F0FF]">
              Você está entrando em
            </span>
            <h1 className="font-display font-black text-4xl sm:text-5xl tracking-tighter mt-3 text-glow-pink">
              {event.name}
            </h1>
            <div className="flex items-center justify-center gap-4 mt-3 text-sm text-white/60">
              <span className="flex items-center gap-1">
                <Calendar className="w-3.5 h-3.5" />
                {new Date(event.date).toLocaleDateString("pt-BR")}
              </span>
              <span className="flex items-center gap-1">
                <Camera className="w-3.5 h-3.5" /> {event.photos_count} fotos
              </span>
            </div>
            {event.description && (
              <p className="text-white/50 mt-4 text-sm">{event.description}</p>
            )}
          </div>

          <form onSubmit={enter} className="glass rounded-2xl p-6" data-testid="guest-name-form">
            <label className="text-xs uppercase tracking-[0.2em] text-white/50">
              Seu nome
            </label>
            <input
              autoFocus
              required
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="input-dark mt-3 text-lg"
              placeholder="Como você quer aparecer?"
              maxLength={40}
              data-testid="guest-name-input"
            />
            <button
              type="submit"
              className="btn-neon w-full mt-6 text-lg py-4"
              data-testid="guest-continue-btn"
            >
              <Camera className="w-5 h-5" /> Tirar foto
            </button>
          </form>

          <div className="text-center mt-6">
            <Link
              to={`/e/${slug}/gallery`}
              className="text-sm text-[#00F0FF] hover:text-white transition-colors inline-flex items-center gap-1"
              data-testid="view-gallery-link"
            >
              <Grid3x3 className="w-4 h-4" /> Ver galeria pública
            </Link>
          </div>

          <div className="text-center mt-8 text-xs text-white/30">
            Powered by <span className="text-[#FF007F]">Festagram</span>
          </div>
        </motion.div>
      </div>
    </div>
  );
}
